// src/api/api.js
import axios from "axios";

const REMOTE_API_BASE_URL = "https://sbt-backend.onrender.com";
const LOCAL_API_BASE_URL = "http://localhost:5000";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "");
const TOKEN_STORAGE_KEY = "sbt_auth_token";

const isLocalOrLanHost = (hostname = "") =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
  /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
  /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(hostname);

const getApiBaseUrl = () => {
  if (ENV_API_BASE_URL) return ENV_API_BASE_URL;

  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;
    if (isLocalOrLanHost(hostname)) {
      return `${protocol}//${hostname}:5000`;
    }
  }

  return REMOTE_API_BASE_URL;
};

const API = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  withCredentials: true,
});

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }
};

export const clearAuthToken = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

const getStoredAuthToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);

API.interceptors.request.use(
  (req) => {
    if (typeof FormData === "undefined" || !(req.data instanceof FormData)) {
      req.headers["Content-Type"] = "application/json";
    }

    const token = getStoredAuthToken();
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }

    const requestBaseUrl = String(req.baseURL || API.defaults.baseURL || "");
    if (requestBaseUrl.includes("ngrok-free.app")) {
      req.headers["ngrok-skip-browser-warning"] = "true";
    }

    return req;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (res) => res,
  (error) => {
    const originalRequest = error.config;
    const activeBaseUrl = String(
      originalRequest?.baseURL || API.defaults.baseURL || ""
    ).replace(/\/$/, "");
    const requestMethod = String(originalRequest?.method || "get").toLowerCase();
    const canRetryLocal =
      !ENV_API_BASE_URL &&
      typeof window !== "undefined" &&
      isLocalOrLanHost(window.location.hostname) &&
      requestMethod === "get";
    const shouldRetryLocal =
      originalRequest &&
      canRetryLocal &&
      !originalRequest._retriedWithLocalApi &&
      activeBaseUrl !== LOCAL_API_BASE_URL &&
      (!error.response || [502, 503, 504].includes(error.response.status));

    if (shouldRetryLocal) {
      originalRequest._retriedWithLocalApi = true;
      originalRequest.baseURL = LOCAL_API_BASE_URL;
      return API(originalRequest);
    }

    if (error.response) {
      const isExpectedAuthCheck =
        error.response.status === 401 && originalRequest?.url === "/auth/me";
      if (error.response.status === 401 && originalRequest?.url !== "/auth/login") {
        clearAuthToken();
      }
      if (!isExpectedAuthCheck) {
        console.error(`API Error [${error.response.status}]:`, error.response.data);
      }
      return Promise.reject(error.response.data);
    }

    if (error.request) {
      console.error("No response received:", {
        url: originalRequest?.url,
        baseURL: originalRequest?.baseURL || API.defaults.baseURL,
      });
      return Promise.reject({
        message: "No response from server. Please try again.",
      });
    }

    console.error("API setup error:", error.message);
    return Promise.reject({ message: error.message });
  }
);

export default API;
